// src/components/user/StewardAreaPicker.jsx
// The Steward Workspace's area picker when there are more areas than tabs (Roadmap #86, R2;
// docs/briefs/2026-10-03-steward-workspace-many-areas.md + addendum 1): type a few letters,
// choose the area. Each match shows the area's name, its parent and its stewards (or
// "Unassigned"). The match list sits in the page flow, pushing the table down, so it needs no
// z-index. Placed in user/ beside its only consumer, StewardWorkspace.jsx.
// Grouped by municipality (Roadmap #87): a steward is offered every area inside an appointment,
// so matches list under their municipality's name, whole groups at a time.
// Extra entries (Roadmap #87, addendum 2): entries that are not areas — an admin's "National"
// entries — head the list whatever is typed, and are chosen through their own callback, so their
// string keys never pass through the numeric area paths.

import React, { useState, useMemo } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { tokens } from '../../styles/designTokens';
import { matchAreaGroups, capGroups, joinNames } from '../../utils/stewardAreas';

// About this many matches are listed, in whole groups; typing narrows them.
const MATCHES_SHOWN_MAX = 12;

const StewardAreaPicker = ({ areas, activeId, onChoose, showStewards, extraEntries = [], activeExtraKey = null, onChooseExtra }) => {
  const { t: tS, formattingLocale } = useTranslation('steward');
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const activeExtra = extraEntries.find(e => e.key === activeExtraKey) || null;
  const [open, setOpen] = useState(activeId == null && !activeExtra);

  const groups = useMemo(() => matchAreaGroups(areas, query, formattingLocale), [areas, query, formattingLocale]);
  const { shown, hidden } = useMemo(() => capGroups(groups, MATCHES_SHOWN_MAX), [groups]);
  const matches = useMemo(() => groups.flatMap(g => g.areas), [groups]);
  const active = activeExtra ? null : areas.find(a => Number(a.boundary_id) === Number(activeId));

  const stewardLine = (area) => {
    const names = area.stewards || [];
    if (names.length === 0) return tS('unassigned');
    const list = joinNames(names, formattingLocale);
    return names.length === 1 ? tS('steward_of_record', { name: list }) : tS('stewards_of_record', { names: list });
  };

  // The chosen area names its parent; a listed match does not, since its group heading does.
  const describe = (area) => [area.parentName, showStewards ? stewardLine(area) : null].filter(Boolean).join(' · ');
  const describeMatch = (area) => (showStewards ? stewardLine(area) : '');

  const choose = (area) => {
    onChoose(Number(area.boundary_id));
    setQuery('');
    setOpen(false);
  };

  const chooseExtra = (entry) => {
    onChooseExtra(entry.key);
    setQuery('');
    setOpen(false);
  };

  const font = { fontFamily: tokens.typography.fontFamily };
  const primary = theme?.primary || tokens.colors.contextLocality;

  return (
    <div style={{ marginBottom: '16px', maxWidth: '480px', ...font }}>
      <label htmlFor="steward-area-picker" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: tokens.colors.textSecondary, marginBottom: '6px' }}>
        {tS('area_picker_label')}
      </label>
      {activeExtra && !open && (
        <div style={{ fontSize: '15px', fontWeight: 700, color: primary, marginBottom: '6px' }}>
          {activeExtra.label}
        </div>
      )}
      {active && !open && (
        <div style={{ fontSize: '15px', fontWeight: 700, color: primary, marginBottom: '6px' }}>
          {active.boundary_name}
          {describe(active) && (
            <span style={{ fontSize: '12px', fontWeight: 400, color: tokens.colors.textSecondary, marginLeft: '8px' }}>
              {describe(active)}
            </span>
          )}
        </div>
      )}
      <input
        id="steward-area-picker"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls="steward-area-picker-list"
        autoComplete="off"
        value={query}
        placeholder={tS('area_picker_placeholder')}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setQuery(''); setOpen(false); }
          if (e.key === 'Enter' && matches.length === 1) choose(matches[0]);
        }}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '14px', ...font,
          border: `1px solid ${tokens.colors.borderSecondary}`, borderRadius: '6px'
        }}
      />
      {activeId == null && !activeExtra && (
        <div style={{ fontSize: '13px', color: tokens.colors.textSecondary, marginTop: '6px' }}>
          {tS('area_picker_choose')}
        </div>
      )}
      {open && (
        <div
          id="steward-area-picker-list"
          role="listbox"
          style={{ marginTop: '6px', border: `1px solid ${tokens.colors.disabledBackground}`, borderRadius: '6px', maxHeight: '320px', overflowY: 'auto' }}
        >
          {extraEntries.map(entry => {
            const selected = entry.key === activeExtraKey;
            return (
              // A raw <button>, by LAYOUT § Usage Rule's documented exception, as the area
              // options below: a listbox option (role="option", aria-selected).
              <button
                key={entry.key}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => chooseExtra(entry)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', ...font,
                  border: 'none', borderBottom: `1px solid ${tokens.colors.disabledBackground}`, cursor: 'pointer',
                  background: selected ? '#EFF6FF' : 'transparent'
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: selected ? 700 : 500, color: selected ? primary : (theme?.text?.primary || '#1F2937') }}>
                  {entry.label}
                </div>
              </button>
            );
          })}
          {matches.length === 0 && (
            <div style={{ padding: '10px', fontSize: '13px', color: tokens.colors.textSecondary }}>
              {tS('area_picker_no_match')}
            </div>
          )}
          {shown.map(group => {
            const headingId = `steward-area-group-${group.key}`;
            return (
              <div key={group.key} role="group" aria-labelledby={headingId}>
                <div id={headingId} style={{
                  padding: '6px 10px 4px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em',
                  textTransform: 'uppercase', color: tokens.colors.textSecondary, background: '#F9FAFB'
                }}>
                  {group.heading}
                </div>
                {group.areas.map(area => {
                  const selected = Number(area.boundary_id) === Number(activeId);
                  return (
                    // A raw <button>, by LAYOUT § Usage Rule's documented exception: a two-line
                    // listbox option (role="option", aria-selected) that StandardButton
                    // variant="inline" cannot express.
                    <button
                      key={area.boundary_id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => choose(area)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', ...font,
                        border: 'none', borderBottom: '1px solid #F3F4F6', cursor: 'pointer',
                        background: selected ? '#EFF6FF' : 'transparent'
                      }}
                    >
                      <div style={{ fontSize: '14px', fontWeight: selected ? 700 : 500, color: selected ? primary : (theme?.text?.primary || '#1F2937') }}>
                        {area.boundary_name}
                      </div>
                      {describeMatch(area) && (
                        <div style={{ fontSize: '12px', color: tokens.colors.textSecondary, marginTop: '2px' }}>
                          {describeMatch(area)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
          {hidden > 0 && (
            <div style={{ padding: '8px 10px', fontSize: '12px', color: tokens.colors.textSecondary }}>
              {tS('area_picker_more', { count: hidden })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StewardAreaPicker;
